import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Trophy, PartyPopper, Loader2, Globe, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METRICS = [
  { k: "applications", l: "Applications" },
  { k: "optimizations", l: "Optimizations" },
  { k: "shortlisted", l: "Shortlisted" },
  { k: "interviews", l: "Interviews" },
  { k: "offers", l: "Offers" },
];
const RANGES = [{ k: "day", l: "Today" }, { k: "week", l: "7d" }, { k: "15day", l: "15d" }, { k: "month", l: "30d" }, { k: "all", l: "All" }];

const chip = (active) => ({
  padding: "0.42rem 0.9rem", borderRadius: 999, fontSize: "0.82rem", cursor: "pointer",
  border: `1.5px solid ${active ? "#6366f1" : "var(--border-main)"}`,
  background: active ? "rgba(99,102,241,0.15)" : "transparent",
  color: active ? "#6366f1" : "var(--text-secondary)",
  fontWeight: active ? 600 : 400, transition: "all 0.15s",
});

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const [features, setFeatures] = useState(null);
  const [tab, setTab] = useState(null);

  const [metric, setMetric] = useState("applications");
  const [range, setRange] = useState("week");
  const [rows, setRows] = useState(null);
  const [wins, setWins] = useState(null);

  useEffect(() => {
    axios.get(`${API}/social/config`).then((r) => {
      const f = r.data.features || {};
      setFeatures(f);
      setTab(f.global_leaderboard_enabled ? "global" : (f.job_offer_board_enabled ? "wins" : "none"));
    }).catch(() => setFeatures({}));
  }, []);

  const loadGlobal = useCallback(async () => {
    setRows(null);
    try {
      const r = await axios.get(`${API}/social/leaderboard?metric=${metric}&range=${range}`);
      setRows(r.data.rows || []);
    } catch { setRows([]); }
  }, [metric, range]);

  const loadWins = useCallback(async () => {
    setWins(null);
    try {
      const r = await axios.get(`${API}/social/job-offers`);
      setWins(r.data.wins || []);
    } catch { setWins([]); }
  }, []);

  useEffect(() => { if (tab === "global") loadGlobal(); }, [tab, loadGlobal]);
  useEffect(() => { if (tab === "wins") loadWins(); }, [tab, loadWins]);

  if (!features) return <div className="analytics-loading" data-testid="leaderboard-loading"><div className="processing-spinner" /></div>;

  if (tab === "none") {
    return (
      <div className="page-container fade-in" data-testid="leaderboard-page">
        <div className="card" style={{ padding: "2.5rem", textAlign: "center" }}>
          <Trophy size={40} style={{ opacity: 0.35 }} />
          <h2>Leaderboards are coming soon</h2>
          <p style={{ color: "var(--text-secondary)" }}>The public leaderboard isn't live yet. Check back later!</p>
        </div>
      </div>
    );
  }

  const tabs = [
    ...(features.global_leaderboard_enabled ? [{ k: "global", l: "Leaderboard", icon: Trophy }] : []),
    ...(features.job_offer_board_enabled ? [{ k: "wins", l: "Job Wins", icon: PartyPopper }] : []),
  ];

  return (
    <div className="page-container fade-in" data-testid="leaderboard-page">
      <div className="page-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Globe size={22} /> Platform Leaderboard</h1>
          <p style={{ color: "var(--text-secondary)" }}>See how the whole community is grinding — and celebrate everyone landing jobs.</p>
        </div>
      </div>

      {tabs.length > 1 && (
        <div style={{ display: "flex", gap: 6, borderBottom: "1px solid var(--border-main)", marginBottom: "1.2rem" }}>
          {tabs.map(({ k, l, icon: Icon }) => (
            <button key={k} data-testid={`lb-tab-${k}`} onClick={() => setTab(k)}
              style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === k ? "#6366f1" : "transparent"}`,
                color: tab === k ? "var(--text-primary)" : "var(--text-secondary)", padding: "0.6rem 0.9rem",
                cursor: "pointer", fontWeight: tab === k ? 600 : 400, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </div>
      )}

      {/* GLOBAL LEADERBOARD */}
      {tab === "global" && (
        <div data-testid="global-leaderboard-tab">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {METRICS.map((m) => (
              <button key={m.k} data-testid={`gl-metric-${m.k}`} onClick={() => setMetric(m.k)} style={chip(metric === m.k)}>{m.l}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {RANGES.map((r) => (
              <button key={r.k} data-testid={`gl-range-${r.k}`} onClick={() => setRange(r.k)} style={chip(range === r.k)}>{r.l}</button>
            ))}
          </div>
          {rows === null ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div>
            : rows.length === 0 ? <div className="card" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>No public activity in this window yet.</div>
            : (
              <div className="card" style={{ padding: 0, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }} data-testid="global-lb-table">
                  <thead><tr style={{ textAlign: "left", color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                    <th style={{ padding: "0.8rem 1rem" }}>Rank</th>
                    <th style={{ padding: "0.8rem 1rem" }}>Member</th>
                    <th style={{ padding: "0.8rem 1rem", textAlign: "right" }}>{METRICS.find((m) => m.k === metric)?.l}</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.username} data-testid={`gl-row-${r.username}`} style={{ borderTop: "1px solid var(--border-main)", background: r.is_me ? "rgba(99,102,241,0.08)" : undefined }}>
                        <td style={{ padding: "0.7rem 1rem", fontWeight: 600 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}</td>
                        <td style={{ padding: "0.7rem 1rem" }}>
                          <span style={{ cursor: "pointer", fontWeight: 500 }} onClick={() => navigate(`/app/u/${r.username}`)}>{r.name || r.username}</span>
                          {r.is_me && <span style={{ fontSize: "0.7rem", marginLeft: 6, color: "#6366f1" }}>(you)</span>}
                          <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>@{r.username}</div>
                        </td>
                        <td style={{ padding: "0.7rem 1rem", textAlign: "right", fontWeight: 700, color: "#6366f1" }}>{r.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* JOB WINS */}
      {tab === "wins" && (
        <div data-testid="job-wins-tab">
          {wins === null ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div>
            : wins.length === 0 ? <div className="card" style={{ padding: "2.5rem", textAlign: "center", color: "var(--text-secondary)" }}><PartyPopper size={36} style={{ opacity: 0.35 }} /><p>No wins shared yet. Be the first to land one! 🎉</p></div>
            : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
                {wins.map((w, i) => (
                  <div key={i} className="card" data-testid={`win-${w.username}-${i}`} style={{ padding: "1.3rem", borderColor: "rgba(16,185,129,0.35)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 42, height: 42, borderRadius: "50%", background: "rgba(16,185,129,0.15)", color: "#10b981", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <PartyPopper size={20} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, cursor: "pointer" }} onClick={() => navigate(`/app/u/${w.username}`)}>{w.name || w.username}</div>
                        <div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>@{w.username} · {timeAgo(w.when)}</div>
                      </div>
                    </div>
                    <p style={{ marginTop: 12, marginBottom: 0 }}>Landed <strong>{w.job_title}</strong>{w.company ? <> at <strong>{w.company}</strong></> : ""} 🎉</p>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}
